import { Link } from "react-router-dom";
import { getAuthor, getCategory, formatDate, type BlogPost } from "@/pages/blog/blogData";
import { categoryBadgeClass, levelBadgeClass } from "@/pages/blog/blogStyles";

interface PostCardProps {
  post: BlogPost;
  variant?: "default" | "featured" | "compact";
}

export default function PostCard({ post, variant = "default" }: PostCardProps) {
  const category = getCategory(post.category);
  const author = getAuthor(post.author);
  const accent = category?.accent ?? "primary";

  if (variant === "compact") {
    return (
      <Link
        to={`/blog/${post.slug}`}
        className="group flex gap-3 rounded-lg border border-background-200 bg-background-50 p-2.5 transition-colors hover:border-primary-300"
      >
        <span className="relative block h-16 w-20 shrink-0 overflow-hidden rounded-md bg-background-200">
          <img
            src={post.image}
            alt={post.imageAlt}
            className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
          />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[11px] font-semibold uppercase tracking-wide text-primary-600">
            {category?.shortName}
          </span>
          <span className="mt-1 line-clamp-2 block text-sm font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
            {post.title}
          </span>
          <span className="mt-1 flex items-center gap-1 text-[11px] text-foreground-400">
            <i className="ri-time-line" aria-hidden="true"></i>
            {post.readingTime}
          </span>
        </span>
      </Link>
    );
  }

  const featured = variant === "featured";

  return (
    <Link
      to={`/blog/${post.slug}`}
      className={`group flex h-full flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50 transition-colors hover:border-primary-300 ${
        featured ? "lg:flex-row" : ""
      }`}
    >
      <span
        className={`relative block overflow-hidden bg-background-200 ${
          featured ? "h-56 w-full lg:h-auto lg:w-1/2" : "h-48 w-full"
        }`}
      >
        <img
          src={post.image}
          alt={post.imageAlt}
          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
        />
      </span>

      <span className={`flex flex-1 flex-col p-5 ${featured ? "lg:p-7" : ""}`}>
        <span className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${categoryBadgeClass(
              accent,
            )}`}
          >
            {category?.name}
          </span>
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${levelBadgeClass(
              post.level,
            )}`}
          >
            {post.level}
          </span>
        </span>

        <span
          className={`mt-3 block font-heading font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 ${
            featured ? "text-xl lg:text-2xl" : "text-lg"
          }`}
        >
          {post.title}
        </span>

        <span
          className={`mt-2 block text-sm leading-relaxed text-foreground-600 ${
            featured ? "" : "line-clamp-2"
          }`}
        >
          {post.excerpt}
        </span>

        <span className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 pt-4 text-xs text-foreground-400">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-3-line" aria-hidden="true"></i>
            {author.name}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-calendar-line" aria-hidden="true"></i>
            {formatDate(post.publishedAt)}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-time-line" aria-hidden="true"></i>
            {post.readingTime}
          </span>
        </span>

        <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary-600">
          Ler artigo
          <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5" aria-hidden="true"></i>
        </span>
      </span>
    </Link>
  );
}